// Platform-neutral durable outbox. Storage and authenticated transport belong to each client.
export function createLibraryQueue({
  read,
  write,
  send,
  onChange = () => {},
  uuid = () => globalThis.crypto.randomUUID(),
  isActive = () => true,
  userId,
}) {
  let entries = [],
    known = new Map(),
    aliases = new Map(),
    localSequence = 0,
    epoch = 0,
    loaded = false,
    serial = Promise.resolve(),
    flushing = null;
  const entity = (kind, category, id) => `${kind}:${category}:${id}`;
  const remember = (kind, row) => {
    if (row) known.set(entity(kind, row.category, row.external_id), row);
  };
  const ready = Promise.resolve()
    .then(read)
    .then((value) => {
      entries = Array.isArray(value)
        ? value.filter(
            (op) =>
              op &&
              op.id &&
              ["shelf", "wishlist", "rating"].includes(op.kind) &&
              op.before !== undefined,
          )
        : [];
      for (const op of entries) if (op.before) remember(op.kind, op.before);
      loaded = true;
      onChange();
    });
  const persist = async () => {
    if (!(await write(entries)))
      throw Error(
        "Не удалось сохранить изменение на устройстве. Освободите место и повторите.",
      );
  };
  const change = () => {
    epoch++;
    if (isActive()) onChange();
  };
  const optimistic = (op) =>
    op.action === "remove"
      ? null
      : {
          ...(op.before || {}),
          ...op.data,
          category: op.category,
          external_id: op.external_id,
          id: op.before?.id ?? op.local_id,
          user_id: userId,
          _pending: true,
        };
  function overlay(kind, rows) {
    let result = [...rows];
    for (const op of entries.filter((op) => op.kind === kind)) {
      const index = result.findIndex(
          (row) =>
            row.category === op.category &&
            String(row.external_id) === op.external_id,
        ),
        row =
          op.action === "remove"
            ? null
            : {
                ...(index < 0 ? op.before || {} : result[index]),
                ...op.data,
                category: op.category,
                external_id: op.external_id,
                id:
                  index < 0 ? (op.before?.id ?? op.local_id) : result[index].id,
                _pending: true,
              };
      if (row && row.user_id == null) row.user_id = userId;
      if (index >= 0) {
        if (row) result[index] = row;
        else result.splice(index, 1);
      } else if (row) result.unshift(row);
    }
    return result;
  }
  const flush = async () => {
    await ready;
    if (flushing) return flushing;
    const visited = new Set();
    flushing = (async () => {
      const blocked = new Set();
      while (true) {
        const op = entries.find((entry) => !visited.has(entry.id));
        if (!op) break;
        visited.add(op.id);
        if (!isActive()) break;
        const key = entity(op.kind, op.category, op.external_id);
        if (op.error || blocked.has(key)) {
          blocked.add(key);
          continue;
        }
        try {
          // The immutable wire payload survives retries after a lost acknowledgement.
          if (!op.wire) {
            op.wire = {
              id: op.id,
              kind: op.kind,
              action: op.action,
              category: op.category,
              external_id: op.external_id,
              data: op.data,
              expected:
                op.before && !op.before._absent
                  ? op.kind === "rating"
                    ? {
                        value: op.before.rating,
                        ...(op.before.sync_revision
                          ? { revision: op.before.sync_revision }
                          : {}),
                      }
                    : { id: op.before.id, revision: op.before.sync_revision }
                  : null,
            };
            await persist();
          }
          const result = await send(op.wire);
          if (!isActive()) break;
          const index = entries.indexOf(op);
          if (index < 0) continue;
          if (result[op.kind])
            aliases.set(
              op.local_id,
              entity(op.kind, op.category, op.external_id),
            );
          for (const kind of ["shelf", "wishlist", "rating"]) {
            const targetKey = entity(kind, op.category, op.external_id),
              row = result[kind];
            if (row) known.set(targetKey, row);
            else known.delete(targetKey);
            // Only rebase actions known to follow this local write; foreign edits still conflict.
            const next = entries
              .slice(index + 1)
              .find(
                (entry) =>
                  entity(entry.kind, entry.category, entry.external_id) ===
                  targetKey,
              );
            if (next && !next.wire) next.before = row || null;
          }
          entries.splice(index, 1);
          await persist();
          change();
        } catch (error) {
          if (!isActive()) break;
          if (error.status === 401) {
            op.auth_required = true;
            await persist();
            change();
            break;
          }
          if (
            !error.status ||
            error.status === 0 ||
            error.status >= 500 ||
            error.status === 429
          )
            break;
          if (error.payload?.current)
            for (const kind of ["shelf", "wishlist", "rating"]) {
              const row = error.payload.current[kind],
                key = entity(kind, op.category, op.external_id);
              if (row) known.set(key, row);
              else known.delete(key);
            }
          op.error = error.message || "Не удалось синхронизировать";
          op.status = error.status;
          await persist();
          blocked.add(key);
          change();
        }
      }
    })().finally(() => {
      flushing = null;
    });
    return flushing;
  };
  const queue = {
    ready,
    flush,
    getState: () => ({
      userId,
      revision: epoch,
      ready: loaded,
      pending: entries.length,
      authRequired: entries.some((op) => op.auth_required),
      errors: entries
        .filter((op) => op.error)
        .map((op) => ({
          id: op.id,
          title: op.before?.title || op.data.title || op.external_id,
          message: op.error,
        })),
    }),
    observe(kind, rows) {
      if (Array.isArray(rows))
        for (const key of known.keys())
          if (key.startsWith(kind + ":")) known.delete(key);
      for (const row of Array.isArray(rows) ? rows : [rows])
        remember(kind, row);
    },
    find(kind, id) {
      return queue
        .snapshot(kind)
        .find(
          (row) =>
            row.id === id ||
            entity(kind, row.category, row.external_id) === aliases.get(id),
        );
    },
    lookup: (kind, category, id) =>
      queue
        .snapshot(kind)
        .find(
          (row) =>
            row.category === category && String(row.external_id) === String(id),
        ),
    overlay,
    snapshot: (kind) =>
      overlay(
        kind,
        [...known.entries()]
          .filter(([key]) => key.startsWith(kind + ":"))
          .map(([, row]) => row),
      ),
    async discard(id) {
      await ready;
      await flushing;
      const op = entries.find((op) => op.id === id);
      if (!op) return;
      if (op.wire && !op.error)
        throw Error(
          "Ответ сервера ещё не получен. Сначала повторите синхронизацию.",
        );
      const key = entity(op.kind, op.category, op.external_id);
      entries = entries.filter(
        (entry) =>
          entry.id !== id &&
          !(
            entry.created >= op.created &&
            entity(entry.kind, entry.category, entry.external_id) === key
          ),
      );
      await persist();
      change();
    },
    mutate(kind, action, row, data = {}) {
      const task = serial
        .catch(() => {})
        .then(async () => {
          await ready;
          if (!isActive()) throw Error("Аккаунт изменился");
          if (entries.length >= 500)
            throw Error("Сначала синхронизируйте накопленные изменения");
          const category = data.category || row?.category,
            external_id = String(data.external_id ?? row?.external_id ?? "");
          if (!category || !external_id)
            throw Error("Сначала загрузите проект при подключении к интернету");
          const key = entity(kind, category, external_id),
            prior = entries
              .filter(
                (op) => entity(op.kind, op.category, op.external_id) === key,
              )
              .at(-1),
            before = prior ? optimistic(prior) : known.get(key) || row || null;
          if (action !== "add" && kind !== "rating" && !before)
            throw Error("Проект не найден в сохранённой библиотеке");
          if (before?._pending && !prior)
            throw Error("Предыдущее изменение ещё синхронизируется");
          const op = {
            id: uuid(),
            kind,
            action,
            category,
            external_id,
            data: { ...data },
            before,
            created: Date.now(),
            local_id: -(Date.now() * 1000 + (localSequence++ % 1000)),
          };
          entries.push(op);
          try {
            await persist();
          } catch (error) {
            entries = entries.filter((entry) => entry !== op);
            throw error;
          }
          change();
          return op;
        });
      serial = task.catch(() => {});
      return task.then(async (op) => {
        await flush();
        if (entries.includes(op) && !op.wire && !op.error) await flush();
        if (entries.includes(op)) return optimistic(op) || { id: row?.id };
        return (
          known.get(entity(kind, op.category, op.external_id)) || {
            id: row?.id,
          }
        );
      });
    },
  };
  return queue;
}
